import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import clsx from "clsx";
import { HeroInlineLogo } from "@/components/dataTable";
import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { TextInput, TextareaInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { getUserLocalLogin, useCurrentUser, userHasGroup } from "@/lib/auth";
import { toast } from "@/lib/toast";
import { isPrivateTimeAccountLogin } from "@/lib/zeiterfassungPermissions";
import {
  EmployeeLockedAutocomplete,
  employeeLoginToOption,
  type EmployeeOption,
} from "@/pages/admin/zeiterfassung/EmployeeLockedAutocomplete";
import {
  ABSENCE_STATUS_LABELS,
  ABSENCE_TYPE_OPTIONS,
  availableOvertimeReductionDays,
  cancelAbsenceRequest,
  createAbsenceRequest,
  deleteAbsenceRequest,
  formatAbsenceDateRange,
  formatAbsenceDays,
  formatBudgetDays,
  getAbsenceTypeLabel,
  isAutoApprovedWithoutReview,
  isPublicPendingAbsenceType,
  isUnavailabilityNotice,
  listAbsenceBudgets,
  listAbsenceRequests,
  MAX_VACATION_CALENDAR_SPAN_DAYS,
  maxBisDateForOvertimeReduction,
  maxBisDateForVacation,
  reservedOvertimeReductionDays,
  type EmployeeAbsenceBudget,
  type AbsenceType,
  type EmployeeAbsenceRequest,
  type HalfDayPart,
  type AbsenceStatus,
} from "@/lib/employeeAbsences";
import {
  calendarDayTitle,
  calendarDaysToMap,
  calendarKindLabel,
  deleteBridgeDayOverride,
  getCalendarDays,
  isAutoAbsenceCalendarDay,
  upsertBridgeDayOverride,
  type EmployeeCalendarDay,
} from "@/lib/employeeCalendar";
import {
  formatHeroAbsenceRange,
  getHeroEmployeeCalendar,
  type HeroEmployeeAbsence,
} from "@/lib/heroEmployeeHr";
import {
  getEmployeeAccountSummary,
  type EmployeeAccountSummary,
} from "@/lib/employeeTimes";
import { AbsenceBudgetOverview } from "@/pages/admin/zeiterfassung/AbsenceBudgetOverview";
import { useRefreshKeyLoad } from "@/pages/admin/zeiterfassung/useRefreshKeyLoad";

interface AbsenceRequestPanelProps {
  refreshKey: number;
  defaultEmployeeLogin: string;
  defaultEmployeeDisplay: string;
  allowEmployeePicker?: boolean;
  /** Büro/GF: genehmigte App-Abwesenheiten mit Begründung löschen */
  allowOfficeDelete?: boolean;
  includeHeroOverlay?: boolean;
  onChanged?: () => void;
}

type DurationMode = "full" | "half_am" | "half_pm";

/** Jahre, deren Urlaubskontingent ein Antrag ab Von berühren kann (inkl. 3-Wochen-Deckel). */
function vacationQuotaYears(dateFrom: string, dateTo: string): number[] {
  const from = dateFrom.slice(0, 10);
  const fromYear = Number(from.slice(0, 4));
  const toYear = dateTo ? Number(dateTo.slice(0, 4)) : fromYear;
  const cap = new Date(`${from}T00:00:00Z`);
  cap.setUTCDate(cap.getUTCDate() + MAX_VACATION_CALENDAR_SPAN_DAYS);
  return [...new Set([fromYear, toYear, cap.getUTCFullYear()])].filter(
    (year) => Number.isInteger(year) && year >= 2000,
  );
}

function durationToPayload(
  dateFrom: string,
  dateTo: string,
  mode: DurationMode,
): { day_fraction: number; half_day_part: HalfDayPart | null } {
  if (dateFrom !== dateTo || mode === "full") {
    return { day_fraction: 1, half_day_part: null };
  }
  if (mode === "half_am") return { day_fraction: 0.5, half_day_part: "am" };
  return { day_fraction: 0.5, half_day_part: "pm" };
}

function formatReviewedAt(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

type AbsenceListFilter = AbsenceStatus | "all" | "active";

const ABSENCE_LIST_FILTER_OPTIONS: Array<{ value: AbsenceListFilter; label: string }> = [
  { value: "active", label: "Aktiv (ausstehend & genehmigt)" },
  { value: "all", label: "Alle" },
  { value: "pending", label: ABSENCE_STATUS_LABELS.pending },
  { value: "approved", label: ABSENCE_STATUS_LABELS.approved },
  { value: "rejected", label: ABSENCE_STATUS_LABELS.rejected },
  { value: "cancelled", label: ABSENCE_STATUS_LABELS.cancelled },
];

interface AutoApprovedBridgeDay {
  workDate: string;
  label: string;
  absenceType: AbsenceType;
}

function RequestRow({
  req,
  onCancel,
  onOfficeDelete,
  allowCancelApprovedOvertime = false,
}: {
  req: EmployeeAbsenceRequest;
  onCancel?: (id: number) => void;
  onOfficeDelete?: (req: EmployeeAbsenceRequest) => void;
  /** Sonderregelung: genehmigten Überstundenabbau selbst zurückziehen */
  allowCancelApprovedOvertime?: boolean;
}) {
  const autoApprovedWithoutReview = isAutoApprovedWithoutReview(req);
  const canCancel =
    Boolean(onCancel) &&
    (req.status === "pending" ||
      (isUnavailabilityNotice(req.absence_type) && req.status === "acknowledged") ||
      (allowCancelApprovedOvertime &&
        req.status === "approved" &&
        req.absence_type === "ueberstundenabbau"));

  return (
    <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-separator px-3 py-2.5">
      <div>
        <div className="font-medium text-label">
          {getAbsenceTypeLabel(req.absence_type)}
          <span className="ml-2 text-sm font-normal text-label-secondary">
            · {formatAbsenceDays(req.total_days)}
          </span>
        </div>
        <div className="text-sm text-label-secondary">{formatAbsenceDateRange(req)}</div>
        {req.note && <div className="mt-1 text-sm text-label-secondary">{req.note}</div>}
        {req.status === "approved" &&
          !autoApprovedWithoutReview &&
          (req.reviewed_by_display || req.reviewed_at) && (
          <div className="mt-1 text-xs text-label-tertiary">
            Freigegeben
            {req.reviewed_by_display ? ` von ${req.reviewed_by_display}` : ""}
            {req.reviewed_at ? ` am ${formatReviewedAt(req.reviewed_at)}` : ""}
          </div>
        )}
        {autoApprovedWithoutReview && (
          <div className="mt-1 text-xs text-label-tertiary">
            Sofort wirksam (keine Freigabe erforderlich)
          </div>
        )}
        {req.status === "rejected" && req.review_comment && (
          <div className="mt-1 text-sm text-status-red">Ablehnung: {req.review_comment}</div>
        )}
        {req.status === "cancelled" && req.review_comment && (
          <div className="mt-1 text-xs text-label-tertiary">{req.review_comment}</div>
        )}
      </div>
      <div className="flex flex-col items-end gap-2">
        <span
          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadgeClass(req.status)}`}
        >
          {absenceStatusLabel(req)}
        </span>
        {canCancel && onCancel && (
          <Button variant="secondary" size="sm" onClick={() => onCancel(req.id)}>
            Zurückziehen
          </Button>
        )}
        {(req.status === "approved" ||
          req.status === "pending" ||
          req.status === "rejected" ||
          req.status === "acknowledged") &&
          onOfficeDelete && (
            <Button variant="danger" size="sm" onClick={() => onOfficeDelete(req)}>
              Löschen
            </Button>
          )}
      </div>
    </div>
  );
}

type AutoApprovedCalendarDay = AutoApprovedBridgeDay & {
  kind: "bridge" | "betriebsferien";
  dayFraction: number;
};

function AutoApprovedCalendarDayRow({ entry }: { entry: AutoApprovedCalendarDay }) {
  const [y, m, d] = entry.workDate.slice(0, 10).split("-");
  const dateLabel = y && m && d ? `${d}.${m}.${y}` : entry.workDate;
  const daysLabel = entry.dayFraction < 1 ? "½ Tag" : "1 Tag";
  const kindLabel = calendarKindLabel(entry.kind);
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-separator px-3 py-2.5">
      <div>
        <div className="font-medium text-label">
          {getAbsenceTypeLabel(entry.absenceType)}
          <span className="ml-2 text-sm font-normal text-label-secondary">· {daysLabel}</span>
        </div>
        <div className="text-sm text-label-secondary">
          {dateLabel} · {entry.label}
        </div>
        <div className="mt-1 text-xs text-label-tertiary">{kindLabel} (automatisch genehmigt)</div>
      </div>
      <span className="rounded-full bg-status-green/15 px-2.5 py-1 text-xs font-semibold text-status-green">
        {ABSENCE_STATUS_LABELS.approved}
      </span>
    </div>
  );
}

function absenceStatusLabel(req: EmployeeAbsenceRequest): string {
  if (isUnavailabilityNotice(req.absence_type)) {
    if (req.status === "pending") return "Hinweis";
    if (req.status === "acknowledged") return "Gelesen";
  }
  return ABSENCE_STATUS_LABELS[req.status];
}

function statusBadgeClass(status: EmployeeAbsenceRequest["status"]): string {
  switch (status) {
    case "pending":
      return "bg-status-orange/15 text-status-orange";
    case "approved":
      return "bg-status-green/15 text-status-green";
    case "rejected":
      return "bg-status-red/15 text-status-red";
    default:
      return "bg-fill-tertiary text-label-secondary";
  }
}

export function AbsenceRequestPanel({
  refreshKey,
  defaultEmployeeLogin,
  defaultEmployeeDisplay,
  allowEmployeePicker = false,
  allowOfficeDelete = false,
  includeHeroOverlay = false,
  onChanged,
}: AbsenceRequestPanelProps) {
  const { user } = useCurrentUser();
  const now = new Date();
  const [selectedEmployee, setSelectedEmployee] = useState<AutocompleteOption<EmployeeOption> | null>(
    () => employeeLoginToOption(defaultEmployeeLogin, defaultEmployeeDisplay),
  );
  const employeeLogin = selectedEmployee?.value.login ?? "";
  const employeeDisplay = selectedEmployee?.value.display ?? "";
  const viewerLogin = getUserLocalLogin(user);
  const canCancelOwnAutoApprovedOvertime =
    isPrivateTimeAccountLogin(employeeLogin) && viewerLogin === employeeLogin;
  const [year, setYear] = useState(now.getFullYear());
  const [listFilter, setListFilter] = useState<AbsenceListFilter>("active");
  const [requests, setRequests] = useState<EmployeeAbsenceRequest[]>([]);
  const [budget, setBudget] = useState<EmployeeAbsenceBudget | null>(null);
  const [accountSummary, setAccountSummary] = useState<EmployeeAccountSummary | null>(null);
  const viewerIsNachunternehmer = userHasGroup(user, "Nachunternehmer");
  const noticeOnly =
    Boolean(selectedEmployee?.value.isNachunternehmer) ||
    accountSummary?.tracks_overtime === false ||
    (viewerIsNachunternehmer && (!allowEmployeePicker || viewerLogin === employeeLogin));
  const [calendarByDay, setCalendarByDay] = useState<Map<string, EmployeeCalendarDay>>(new Map());
  const [bridgeDefaultType, setBridgeDefaultType] = useState<AbsenceType>("urlaub");
  const [heroAbsences, setHeroAbsences] = useState<HeroEmployeeAbsence[]>([]);
  const [heroLinked, setHeroLinked] = useState(false);
  const [heroPartnerName, setHeroPartnerName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [absenceType, setAbsenceType] = useState<AbsenceType>("urlaub");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [vacationAvailableByYear, setVacationAvailableByYear] = useState<Record<number, number>>(
    {},
  );
  const [extraHolidayDates, setExtraHolidayDates] = useState<ReadonlySet<string>>(new Set());
  const [durationMode, setDurationMode] = useState<DurationMode>("full");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (noticeOnly) {
      setAbsenceType("nicht_verfuegbar");
      setDurationMode("full");
      return;
    }
    setAbsenceType((current) => (current === "nicht_verfuegbar" ? "urlaub" : current));
  }, [noticeOnly]);

  const [deleteTarget, setDeleteTarget] = useState<EmployeeAbsenceRequest | null>(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  const isSingleDay = Boolean(dateFrom && dateTo && dateFrom === dateTo);

  useEffect(() => {
    if (!allowEmployeePicker && defaultEmployeeLogin) {
      setSelectedEmployee(
        employeeLoginToOption(defaultEmployeeLogin, defaultEmployeeDisplay),
      );
    }
  }, [allowEmployeePicker, defaultEmployeeLogin, defaultEmployeeDisplay]);

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!employeeLogin) {
      setRequests([]);
      setBudget(null);
      setAccountSummary(null);
      setCalendarByDay(new Map());
      setHeroAbsences([]);
      setHeroLinked(false);
      setHeroPartnerName(null);
      setLoading(false);
      return;
    }
    if (!options?.silent) setLoading(true);
    try {
      // Hero immer laden für Budget-Übersicht / Urlaubstabelle; Overlay-UI bleibt optional.
      const [rows, budgets, calendar, heroData, summary] = await Promise.all([
        listAbsenceRequests({ user_login: employeeLogin, year }),
        listAbsenceBudgets({ user_login: employeeLogin, year }),
        getCalendarDays({ user_login: employeeLogin, year }),
        getHeroEmployeeCalendar({ year, user_login: employeeLogin }).catch(() => null),
        getEmployeeAccountSummary({ user_login: employeeLogin, year }).catch(() => null),
      ]);
      setRequests(rows);
      setBudget(budgets[0] ?? null);
      setAccountSummary(summary);
      setCalendarByDay(calendarDaysToMap(calendar.days));
      setBridgeDefaultType(calendar.bridge_default_absence_type);
      if (heroData) {
        setHeroAbsences(heroData.absences);
        setHeroLinked(heroData.linked);
        setHeroPartnerName(heroData.partner_name);
      } else {
        setHeroAbsences([]);
        setHeroLinked(false);
        setHeroPartnerName(null);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Anträge konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [employeeLogin, year]);

  useRefreshKeyLoad(load, refreshKey);

  useEffect(() => {
    setVacationAvailableByYear({});
    setExtraHolidayDates(new Set());
  }, [employeeLogin]);

  const panelAvailableVacationDays = Number(
    accountSummary?.available_vacation_days ?? budget?.available_vacation_days ?? 0,
  );
  const availableByYear = useMemo(() => {
    const panelDays = Number.isFinite(panelAvailableVacationDays) ? panelAvailableVacationDays : 0;
    return { ...vacationAvailableByYear, [year]: panelDays };
  }, [panelAvailableVacationDays, vacationAvailableByYear, year]);

  useEffect(() => {
    if (!employeeLogin || absenceType !== "urlaub" || !dateFrom) return;
    const needed = vacationQuotaYears(dateFrom, dateTo).filter(
      (quotaYear) => availableByYear[quotaYear] === undefined,
    );
    if (needed.length === 0) return;
    let cancelled = false;
    void (async () => {
      try {
        const rows = await Promise.all(
          needed.map(async (quotaYear) => {
            const [budgets, calendar] = await Promise.all([
              listAbsenceBudgets({ user_login: employeeLogin, year: quotaYear }),
              getCalendarDays({ user_login: employeeLogin, year: quotaYear }),
            ]);
            const available = Number(budgets[0]?.available_vacation_days ?? 0);
            return {
              quotaYear,
              available: Number.isFinite(available) ? available : 0,
              holidays: calendar.days
                .filter((day) => day.kind === "holiday")
                .map((day) => day.work_date.slice(0, 10)),
            };
          }),
        );
        if (cancelled) return;
        setVacationAvailableByYear((prev) => {
          const next = { ...prev };
          for (const row of rows) next[row.quotaYear] = row.available;
          return next;
        });
        setExtraHolidayDates((prev) => {
          const next = new Set(prev);
          for (const row of rows) {
            for (const iso of row.holidays) next.add(iso);
          }
          return next;
        });
      } catch {
        // Das Backend prüft das Kontingent beim Speichern erneut.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [absenceType, availableByYear, dateFrom, dateTo, employeeLogin]);

  const holidayDates = useMemo(() => {
    const set = new Set<string>(extraHolidayDates);
    for (const day of calendarByDay.values()) {
      if (day.kind === "holiday") set.add(day.work_date.slice(0, 10));
    }
    return set;
  }, [calendarByDay, extraHolidayDates]);

  const requestYear = dateFrom ? Number(dateFrom.slice(0, 4)) : year;
  const requestYearQuota = availableByYear[requestYear];
  const requestQuotaKnown = requestYearQuota !== undefined;
  const availableVacationDays = requestYearQuota ?? Number.NaN;

  // Arbeitstage laut Arbeitszeitmodell (z. B. 4-Tage-Woche): freie Wochentage
  // verbrauchen kein Kontingent und verlängern den erlaubten Zeitraum entsprechend.
  const workWeekdays = useMemo<ReadonlySet<number> | null>(() => {
    const raw = accountSummary?.work_weekdays;
    if (!Array.isArray(raw) || raw.length === 0) return null;
    return new Set(raw.map((v) => Number(v)).filter((v) => v >= 1 && v <= 7));
  }, [accountSummary?.work_weekdays]);

  const overtimeAvailableDays = useMemo(() => {
    const reserved = reservedOvertimeReductionDays(
      requests,
      accountSummary?.overtime_as_of,
    );
    return availableOvertimeReductionDays({
      overtimeBalanceHours: accountSummary?.overtime_balance_hours,
      averageDailySollHours: accountSummary?.average_daily_soll_hours,
      reservedDays: reserved,
    });
  }, [accountSummary, requests]);

  const maxDateTo = useMemo(() => {
    if (!dateFrom) return null;
    if (absenceType === "urlaub") {
      return maxBisDateForVacation({
        dateFrom,
        availableVacationDays: requestQuotaKnown ? availableVacationDays : 0,
        availableVacationDaysByYear: availableByYear,
        holidayDates,
        workWeekdays,
      });
    }
    if (absenceType === "ueberstundenabbau") {
      return maxBisDateForOvertimeReduction({
        dateFrom,
        availableDays: overtimeAvailableDays,
        holidayDates,
        workWeekdays,
      });
    }
    return null;
  }, [
    absenceType,
    availableByYear,
    availableVacationDays,
    dateFrom,
    holidayDates,
    overtimeAvailableDays,
    requestQuotaKnown,
    workWeekdays,
  ]);

  useEffect(() => {
    if (dateFrom && !dateTo) {
      setDateTo(dateFrom);
      return;
    }
    if (!dateFrom || !dateTo) return;
    if (dateTo < dateFrom) {
      setDateTo(dateFrom);
      return;
    }
    if (maxDateTo && dateTo > maxDateTo) {
      setDateTo(maxDateTo);
    }
  }, [dateFrom, dateTo, maxDateTo]);

  useEffect(() => {
    if (!isSingleDay && durationMode !== "full") {
      setDurationMode("full");
    }
  }, [isSingleDay, durationMode]);

  const rangeLimitHint = useMemo(() => {
    if (absenceType === "urlaub") {
      if (!requestQuotaKnown) {
        return "Bis maximal 3 Wochen. Das Urlaubskontingent des gewählten Jahres wird geladen.";
      }
      if (!Number.isFinite(availableVacationDays) || availableVacationDays <= 0) {
        return `Kein Resturlaub in ${requestYear} verfügbar – Urlaub kann nicht beantragt werden.`;
      }
      const toYear = dateTo ? Number(dateTo.slice(0, 4)) : requestYear;
      const toYearQuota = availableByYear[toYear];
      if (toYear !== requestYear && toYearQuota !== undefined) {
        return `Bis maximal 3 Wochen. Verfügbar: ${formatBudgetDays(availableVacationDays)} Tage in ${requestYear} und ${formatBudgetDays(toYearQuota)} Tage in ${toYear}. Halbe Tage: Von und Bis auf denselben Tag setzen.`;
      }
      const yearNote = requestYear !== year ? ` in ${requestYear}` : "";
      return `Bis maximal 3 Wochen bzw. ${formatBudgetDays(availableVacationDays)} verfügbare Urlaubstage${yearNote}. Halbe Tage: Von und Bis auf denselben Tag setzen.`;
    }
    if (absenceType === "ueberstundenabbau") {
      if (overtimeAvailableDays <= 0) {
        return "Kein Überstundenabbau verfügbar – auf dem Konto sind keine Tage frei.";
      }
      return `Maximal ${formatBudgetDays(overtimeAvailableDays)} Tage Überstundenabbau laut aktuellem Kontostand. Halbe Tage: Von und Bis auf denselben Tag setzen.`;
    }
    return null;
  }, [
    absenceType,
    availableByYear,
    availableVacationDays,
    dateTo,
    overtimeAvailableDays,
    requestQuotaKnown,
    requestYear,
    year,
  ]);

  const selectedCalendarDay =
    isSingleDay && dateFrom ? calendarByDay.get(dateFrom) ?? null : null;

  const upcomingAutoAbsenceDays = [...calendarByDay.values()]
    .filter((d) => isAutoAbsenceCalendarDay(d) && !d.works_instead)
    .sort((a, b) => a.work_date.localeCompare(b.work_date));

  const autoApprovedCalendarDays = useMemo((): AutoApprovedCalendarDay[] => {
    return [...calendarByDay.values()]
      .filter(
        (d) =>
          isAutoAbsenceCalendarDay(d) &&
          !d.works_instead &&
          d.is_bridge_default &&
          d.effective_absence_type &&
          d.work_date.startsWith(String(year)),
      )
      .map((d) => ({
        workDate: d.work_date.slice(0, 10),
        label: d.label,
        absenceType: d.effective_absence_type!,
        kind: d.kind as "bridge" | "betriebsferien",
        dayFraction: d.day_fraction ?? 1,
      }))
      .sort((a, b) => a.workDate.localeCompare(b.workDate));
  }, [calendarByDay, year]);

  const filteredRequests = useMemo(() => {
    return requests.filter((req) => {
      if (listFilter === "all") return true;
      if (listFilter === "active") {
        return (
          req.status === "pending" ||
          req.status === "approved" ||
          (isUnavailabilityNotice(req.absence_type) && req.status === "acknowledged")
        );
      }
      return req.status === listFilter;
    });
  }, [requests, listFilter]);

  const pendingRequests = useMemo(
    () => filteredRequests.filter((req) => req.status === "pending"),
    [filteredRequests],
  );
  const approvedRequests = useMemo(
    () => filteredRequests.filter((req) => req.status === "approved"),
    [filteredRequests],
  );
  const otherRequests = useMemo(
    () =>
      filteredRequests.filter((req) => req.status === "rejected" || req.status === "cancelled"),
    [filteredRequests],
  );

  const showApprovedAutoDays =
    (listFilter === "all" || listFilter === "active" || listFilter === "approved") &&
    autoApprovedCalendarDays.length > 0;

  const hasListContent =
    filteredRequests.length > 0 || (showApprovedAutoDays && autoApprovedCalendarDays.length > 0);

  const handleWorkOnAutoAbsenceDay = async (day: EmployeeCalendarDay) => {
    const workDate = day.work_date.slice(0, 10);
    const kindLabel = calendarKindLabel(day.kind);
    try {
      await upsertBridgeDayOverride({
        work_date: workDate,
        absence_type: null,
        user_login: employeeLogin,
        note: `An ${kindLabel}-Tag arbeiten`,
      });
      toast.success(`${kindLabel}: Override gespeichert – Sie planen zu arbeiten.`);
      onChanged?.();
      await load({ silent: true });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Override fehlgeschlagen.");
    }
  };

  const handleRequestAutoAbsenceAlternative = (day: EmployeeCalendarDay) => {
    setDateFrom(day.work_date.slice(0, 10));
    setDateTo(day.work_date.slice(0, 10));
    setAbsenceType("ueberstundenabbau");
    if ((day.day_fraction ?? 1) < 1 && day.half_day_part === "am") {
      setDurationMode("half_am");
    } else if ((day.day_fraction ?? 1) < 1) {
      setDurationMode("half_pm");
    } else {
      setDurationMode("full");
    }
    setNote("");
  };

  const autoAbsenceDayStatusLabel = (day: EmployeeCalendarDay): string => {
    if (day.works_instead) return "Arbeit geplant";
    if (day.absence_request_status === "pending") return "Abweichender Antrag offen";
    if (day.is_bridge_default && day.effective_absence_type) {
      const half = (day.day_fraction ?? 1) < 1 ? " · ½ Tag" : "";
      return `${getAbsenceTypeLabel(day.effective_absence_type)} (automatisch genehmigt)${half}`;
    }
    if (day.effective_absence_type) {
      return getAbsenceTypeLabel(day.effective_absence_type);
    }
    return "Offen";
  };

  const handleClearBridgeOverride = async (workDate: string) => {
    try {
      await deleteBridgeDayOverride({ work_date: workDate, user_login: employeeLogin });
      await load({ silent: true });
    } catch {
      /* override may not exist */
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!employeeLogin || !dateFrom || !dateTo) {
      toast.error("Bitte Mitarbeiter und Zeitraum ausfüllen.");
      return;
    }
    if (dateTo < dateFrom) {
      toast.error("Das Enddatum darf nicht vor dem Startdatum liegen.");
      return;
    }
    if (!noticeOnly && absenceType === "urlaub") {
      if (requestQuotaKnown && (!Number.isFinite(availableVacationDays) || availableVacationDays <= 0)) {
        toast.error(`Kein Resturlaub in ${requestYear} verfügbar.`);
        return;
      }
      if (maxDateTo && dateTo > maxDateTo) {
        toast.error(
          `Urlaub darf maximal bis ${maxDateTo.split("-").reverse().join(".")} beantragt werden (3 Wochen oder Restanspruch).`,
        );
        return;
      }
      if (
        durationMode !== "full" &&
        requestQuotaKnown &&
        availableVacationDays < 0.5
      ) {
        toast.error(`Für einen halben Urlaubstag ist in ${requestYear} nicht genug Resturlaub vorhanden.`);
        return;
      }
    }
    if (!noticeOnly && absenceType === "ueberstundenabbau") {
      if (overtimeAvailableDays <= 0) {
        toast.error("Kein Überstundenabbau verfügbar.");
        return;
      }
      if (maxDateTo && dateTo > maxDateTo) {
        toast.error(
          `Überstundenabbau darf maximal bis ${maxDateTo.split("-").reverse().join(".")} beantragt werden.`,
        );
        return;
      }
      if (durationMode !== "full" && overtimeAvailableDays < 0.5) {
        toast.error("Für einen halben Tag Überstundenabbau reicht der Kontostand nicht.");
        return;
      }
    }

    setSaving(true);
    try {
      const duration = noticeOnly
        ? { day_fraction: 1 as const, half_day_part: null }
        : durationToPayload(dateFrom, dateTo, durationMode);
      const submitType = noticeOnly ? "nicht_verfuegbar" : absenceType;
      const created = await createAbsenceRequest({
        absence_type: submitType,
        date_from: dateFrom,
        date_to: dateTo,
        ...duration,
        note: note.trim() || undefined,
        user_login: employeeLogin,
        user_display: employeeDisplay,
      });
      if (!noticeOnly && isAutoAbsenceCalendarDay(selectedCalendarDay)) {
        await handleClearBridgeOverride(dateFrom);
      }
      if (noticeOnly) {
        toast.success("Nicht verfügbar ist im Montageplan eingetragen.");
      } else if (created.status === "approved") {
        toast.success(
          absenceType === "ueberstundenabbau"
            ? "Überstundenabbau wurde eingetragen (keine Freigabe erforderlich)."
            : "Abwesenheit wurde genehmigt.",
        );
      } else if (isPublicPendingAbsenceType(absenceType)) {
        toast.success(
          "Krankheitstage sind beantragt und im Montageplan sichtbar (warten auf Freigabe).",
        );
      } else {
        toast.success("Abwesenheitsantrag wurde eingereicht und wartet auf Freigabe.");
      }
      setDateFrom("");
      setDateTo("");
      setDurationMode("full");
      setNote("");
      onChanged?.();
      await load({ silent: true });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Antrag konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (id: number) => {
    try {
      await cancelAbsenceRequest(id);
      onChanged?.();
      await load({ silent: true });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Zurückziehen fehlgeschlagen.");
    }
  };

  const closeOfficeDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteReason("");
  };

  const confirmOfficeDelete = async () => {
    if (!deleteTarget) return;
    const reason = deleteReason.trim();
    if (reason.length < 3) {
      toast.error("Bitte eine Begründung mit mindestens 3 Zeichen angeben.");
      return;
    }
    setDeleting(true);
    try {
      await deleteAbsenceRequest(deleteTarget.id, reason);
      toast.success("Abwesenheit wurde gelöscht (Begründung gespeichert).");
      setDeleteTarget(null);
      setDeleteReason("");
      onChanged?.();
      await load({ silent: true });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setDeleting(false);
    }
  };

  const officeDeleteHandler = allowOfficeDelete
    ? (req: EmployeeAbsenceRequest) => {
        setDeleteTarget(req);
        setDeleteReason("");
      }
    : undefined;

  return (
    <div className="space-y-4">
      {!loading && !noticeOnly && (
        <AbsenceBudgetOverview
          year={year}
          budget={budget}
          appRequests={requests}
          heroAbsences={heroAbsences}
          heroLinked={heroLinked}
        />
      )}

      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="space-y-4 rounded-ios border border-separator bg-card p-4 shadow-ios md:p-5"
      >
        <div>
          <h2 className="text-lg font-semibold text-label">
            {noticeOnly ? "Nicht verfügbar eintragen" : "Abwesenheit beantragen"}
          </h2>
          <p className="mt-1 text-sm text-label-secondary">
            {noticeOnly
              ? "Tragen Sie ein, wann Sie nicht da sind. Optional ein Text, der in der Schraffur des Montageplans steht. Die Geschäftsführung sieht den Eintrag als Hinweis."
              : isPublicPendingAbsenceType(absenceType)
                ? "Krankheitstage erscheinen sofort im Montageplan, auch für Monteure. Die Freigabe bleibt nötig. Liegt die Krankheit im Urlaub, wird der Urlaub nach Freigabe wieder gutgeschrieben."
                : "Anträge werden an die Geschäftsführung zur Freigabe weitergeleitet."}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {allowEmployeePicker ? (
            <EmployeeLockedAutocomplete
              value={selectedEmployee}
              onChange={setSelectedEmployee}
            />
          ) : (
            <label className="space-y-1">
              <span className="eh-label">Mitarbeiter</span>
              <div className="eh-input flex items-center bg-fill-tertiary/40 text-label">
                {employeeDisplay || employeeLogin}
              </div>
            </label>
          )}

          {!noticeOnly && (
          <div className="space-y-1 md:col-span-2">
            <span className="eh-label">Art</span>
            <div
              className="grid grid-cols-2 gap-2 sm:grid-cols-3"
              role="radiogroup"
              aria-label="Art der Abwesenheit"
              data-tour="abwesenheit-art"
            >
              {ABSENCE_TYPE_OPTIONS.map((opt) => {
                const active = absenceType === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={clsx(
                      "h-11 truncate rounded-lg border px-2 text-sm font-medium transition",
                      active
                        ? "border-accent bg-accent text-label-on-accent shadow-sm"
                        : "border-separator bg-card text-label hover:border-accent/60 active:bg-fill-tertiary",
                    )}
                    onClick={() => setAbsenceType(opt.value)}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          )}

          <div className="grid grid-cols-2 gap-3 md:col-span-2">
            <label className="space-y-1" data-tour="abwesenheit-von">
              <span className="eh-label">Von</span>
              <input
                type="date"
                className="eh-input h-12 w-full text-base md:h-auto md:text-sm"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                required
              />
            </label>

            <label className="space-y-1" data-tour="abwesenheit-bis">
              <span className="eh-label">Bis</span>
              <input
                type="date"
                className="eh-input h-12 w-full text-base md:h-auto md:text-sm"
                value={dateTo}
                min={dateFrom || undefined}
                max={maxDateTo || undefined}
                onChange={(e) => setDateTo(e.target.value)}
                required
              />
            </label>
          </div>

          {!noticeOnly && rangeLimitHint && (
            <p className="text-xs text-label-secondary md:col-span-2">{rangeLimitHint}</p>
          )}

          {!noticeOnly && (
          <div className="space-y-1 md:col-span-2">
            <span className="eh-label">Umfang</span>
            <SegmentedControl
              value={isSingleDay ? durationMode : "full"}
              onChange={setDurationMode}
              name="Umfang"
              size="md"
              disabled={!isSingleDay}
              className="flex w-full md:inline-flex md:w-auto"
              options={[
                { value: "full", label: "Ganzer Tag" },
                { value: "half_am", label: "Vormittag" },
                { value: "half_pm", label: "Nachmittag" },
              ]}
            />
            {!isSingleDay && (
              <p className="text-xs text-label-secondary">
                Vormittag oder Nachmittag nur bei einem einzelnen Tag (Von und Bis gleich).
              </p>
            )}
          </div>
          )}

          {!noticeOnly && selectedCalendarDay && (
            <div className="md:col-span-2">
              <Alert
                variant={selectedCalendarDay.kind === "holiday" ? "warning" : "info"}
                title={
                  selectedCalendarDay.kind === "holiday"
                    ? `Feiertag: ${selectedCalendarDay.label}`
                    : `${calendarKindLabel(selectedCalendarDay.kind)}: ${selectedCalendarDay.label}`
                }
              >
                {isAutoAbsenceCalendarDay(selectedCalendarDay) && (
                  <p className="text-sm">
                    {selectedCalendarDay.is_bridge_default ? (
                      <>
                        Standardmäßig ist{" "}
                        <strong>
                          {getAbsenceTypeLabel(
                            selectedCalendarDay.effective_absence_type ??
                              (selectedCalendarDay.kind === "betriebsferien"
                                ? "urlaub"
                                : bridgeDefaultType),
                          )}
                        </strong>{" "}
                        bereits automatisch genehmigt eingetragen
                        {(selectedCalendarDay.day_fraction ?? 1) < 1
                          ? " (½ Tag, Nachmittag)."
                          : "."}
                      </>
                    ) : selectedCalendarDay.works_instead ? (
                      <>
                        Für diesen {calendarKindLabel(selectedCalendarDay.kind)}-Tag ist Arbeit
                        geplant.
                      </>
                    ) : (
                      <>
                        Abweichende Abwesenheit:{" "}
                        <strong>
                          {selectedCalendarDay.effective_absence_type
                            ? getAbsenceTypeLabel(selectedCalendarDay.effective_absence_type)
                            : "—"}
                        </strong>
                        {selectedCalendarDay.absence_request_status === "pending" && " (Antrag offen)"}
                      </>
                    )}
                  </p>
                )}
                {isAutoAbsenceCalendarDay(selectedCalendarDay) &&
                  !selectedCalendarDay.works_instead && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => handleRequestAutoAbsenceAlternative(selectedCalendarDay)}
                    >
                      Abweichend beantragen
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => void handleWorkOnAutoAbsenceDay(selectedCalendarDay)}
                    >
                      An diesem Tag arbeiten
                    </Button>
                  </div>
                )}
                {isAutoAbsenceCalendarDay(selectedCalendarDay) && (
                  <p className="mt-2 text-xs text-label-secondary">
                    Ein abweichender Antrag (z. B. Überstundenabbau) ersetzt den automatischen Urlaub
                    nach Freigabe. „An diesem Tag arbeiten“ trägt keinen Urlaub ein.
                    {selectedCalendarDay.kind === "betriebsferien" &&
                      " An Heiligabend und Silvester zählt nur ein halber Urlaubstag."}
                  </p>
                )}
              </Alert>
            </div>
          )}

          <div className="md:col-span-2">
            <TextInput
              label={noticeOnly ? "Text für den Montageplan (optional)" : "Anmerkung (optional)"}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={
                noticeOnly ? "z. B. andere Baustelle, nicht im Haus" : "z. B. Vertretung, Arztbesuch …"
              }
            />
          </div>
        </div>

        <Button
          type="submit"
          data-tour="abwesenheit-einreichen"
          variant="primary"
          size="lg"
          className="w-full md:w-auto"
          disabled={saving || !employeeLogin}
        >
          {saving ? "Wird eingereicht…" : noticeOnly ? "Eintragen" : "Antrag einreichen"}
        </Button>
      </form>

      {!noticeOnly && upcomingAutoAbsenceDays.length > 0 && (
        <div className="rounded-ios border border-separator bg-card p-4 shadow-ios">
          <h3 className="text-sm font-semibold text-label">
            Brückentage &amp; Betriebsferien {year}
          </h3>
          <p className="mt-1 text-xs text-label-secondary">
            Urlaub ist für Brückentage und Betriebsferien standardmäßig bereits genehmigt. Nur bei
            Abweichung ist ein Antrag nötig. An Heiligabend und Silvester zählt ein halber Tag.
          </p>
          <ul className="mt-2 space-y-2">
            {upcomingAutoAbsenceDays.map((day) => (
              <li
                key={day.work_date}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-separator px-3 py-2 text-sm"
              >
                <div>
                  <div className="font-medium text-label">
                    {day.work_date.slice(8, 10)}.{day.work_date.slice(5, 7)}.{day.work_date.slice(0, 4)}
                  </div>
                  <div className="text-xs text-label-secondary" title={calendarDayTitle(day)}>
                    {day.label} · {autoAbsenceDayStatusLabel(day)}
                  </div>
                </div>
                {!day.works_instead && day.absence_request_status !== "pending" && (
                  <div className="flex flex-wrap gap-1">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => handleRequestAutoAbsenceAlternative(day)}
                    >
                      Abweichend beantragen
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => void handleWorkOnAutoAbsenceDay(day)}
                    >
                      Arbeiten
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-ios border border-separator bg-card p-4 shadow-ios">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-label">Abwesenheiten (App)</h3>
            <p className="mt-1 text-xs text-label-secondary">
              Ausstehende Anträge sowie genehmigte und freigegebene Abwesenheiten aus der App.
              Brückentage und Betriebsferien erscheinen automatisch. Hero-Abwesenheiten stehen
              separat darunter und sind im Montageplan zusätzlich sichtbar.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="space-y-1">
              <span className="eh-label">Filter</span>
              <select
                className="eh-input min-w-[12rem]"
                value={listFilter}
                onChange={(e) => setListFilter(e.target.value as AbsenceListFilter)}
              >
                {ABSENCE_LIST_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1">
              <span className="eh-label">Jahr</span>
              <select
                className="eh-input"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : !hasListContent ? (
          <p className="text-sm text-label-secondary">
            {listFilter === "active"
              ? `Keine ausstehenden oder genehmigten Abwesenheiten in ${year}.`
              : `Keine Abwesenheiten für die gewählten Filter in ${year}.`}
          </p>
        ) : (
          <div className="space-y-6">
            {pendingRequests.length > 0 && (
              <section className="space-y-2">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">
                  Ausstehend ({pendingRequests.length})
                </h4>
                {pendingRequests.map((req) => (
                  <RequestRow
                    key={req.id}
                    req={req}
                    onCancel={(id) => void handleCancel(id)}
                    onOfficeDelete={officeDeleteHandler}
                  />
                ))}
              </section>
            )}

            {(approvedRequests.length > 0 || showApprovedAutoDays) && (
              <section className="space-y-2">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">
                  Genehmigt & freigegeben ({approvedRequests.length + (showApprovedAutoDays ? autoApprovedCalendarDays.length : 0)})
                </h4>
                {approvedRequests.map((req) => (
                  <RequestRow
                    key={req.id}
                    req={req}
                    onCancel={
                      canCancelOwnAutoApprovedOvertime
                        ? (id) => void handleCancel(id)
                        : undefined
                    }
                    allowCancelApprovedOvertime={canCancelOwnAutoApprovedOvertime}
                    onOfficeDelete={officeDeleteHandler}
                  />
                ))}
                {showApprovedAutoDays &&
                  autoApprovedCalendarDays.map((entry) => (
                    <AutoApprovedCalendarDayRow
                      key={`${entry.kind}-${entry.workDate}`}
                      entry={entry}
                    />
                  ))}
              </section>
            )}

            {otherRequests.length > 0 && (
              <section className="space-y-2">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">
                  Abgelehnt & zurückgezogen ({otherRequests.length})
                </h4>
                {otherRequests.map((req) => (
                  <RequestRow key={req.id} req={req} />
                ))}
              </section>
            )}
          </div>
        )}
      </div>

      <Dialog
        open={deleteTarget != null}
        onClose={closeOfficeDelete}
        size="sm"
        title="Abwesenheit löschen?"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={closeOfficeDelete} disabled={deleting}>
              Abbrechen
            </Button>
            <Button
              type="button"
              variant="danger"
              loading={deleting}
              disabled={deleteReason.trim().length < 3}
              onClick={() => void confirmOfficeDelete()}
            >
              Mit Begründung löschen
            </Button>
          </>
        }
      >
        {deleteTarget ? (
          <div className="space-y-3 text-sm">
            <div className="rounded-ios border border-separator bg-card-elevated p-3">
              <div className="font-semibold text-label">
                {getAbsenceTypeLabel(deleteTarget.absence_type)} ·{" "}
                {formatAbsenceDays(deleteTarget.total_days)}
              </div>
              <div className="text-label">{formatAbsenceDateRange(deleteTarget)}</div>
            </div>
            <TextareaInput
              label="Begründung (Pflicht)"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Warum wird diese Abwesenheit gelöscht?"
              rows={3}
              required
            />
            <p className="text-xs text-label-secondary">
              Der Eintrag verschwindet aus Montageplan und Jahresübersicht. Die Begründung bleibt
              gespeichert.
            </p>
          </div>
        ) : null}
      </Dialog>

      {includeHeroOverlay && (
        <div className="rounded-ios border border-violet-500/25 bg-violet-500/5 p-4 shadow-ios">
          <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-label">Abwesenheiten in Hero</h3>
              <p className="mt-1 text-xs text-label-secondary">
                {heroLinked && heroPartnerName
                  ? `Daten aus Hero für ${heroPartnerName} – nur Anzeige. Erscheinen auch im Montageplan, aber nicht unter „Abwesenheiten (App)“. Löschen/Ändern nur in Hero.`
                  : "Kein Hero-Partner verknüpft."}
              </p>
            </div>
          </div>

          {!heroLinked ? (
            <p className="text-sm text-label-secondary">
              Hero-Abwesenheiten können nicht geladen werden (kein passender Hero-Mitarbeiter).
            </p>
          ) : heroAbsences.length === 0 ? (
            <p className="text-sm text-label-secondary">Keine Abwesenheiten in Hero für {year}.</p>
          ) : (
            <div className="space-y-2">
              {heroAbsences
                .slice()
                .sort((a, b) => a.start.localeCompare(b.start))
                .map((absence) => (
                  <div
                    key={`hero-${absence.id ?? absence.start}-${absence.end}-${absence.absence_type}`}
                    className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-violet-500/20 bg-card px-3 py-2.5"
                  >
                    <div>
                      <div className="inline-flex items-center gap-1.5 font-medium text-label">
                        <HeroInlineLogo />
                        {getAbsenceTypeLabel(absence.absence_type)}
                      </div>
                      <div className="text-sm text-label-secondary">
                        {formatHeroAbsenceRange(absence)}
                      </div>
                      {absence.comment && (
                        <div className="mt-1 text-sm text-label-secondary">{absence.comment}</div>
                      )}
                      {absence.hero_type && (
                        <div className="mt-0.5 text-[10px] text-label-tertiary">
                          Hero-Typ: {absence.hero_type}
                        </div>
                      )}
                    </div>
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadgeClass(absence.status)}`}
                    >
                      {ABSENCE_STATUS_LABELS[absence.status]}
                    </span>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
